import { useCallback, useEffect, useState } from 'react';

export interface InfiniteCarousel<T> {
  /** Items to render as slides, incl. a leading clone of the last item and a
   *  trailing clone of the first (so ends wrap with no visible rewind). */
  slides: T[];
  /** Physical slide index to translate to (`translateX(-pos * 100%)`). */
  pos: number;
  /** Logical active item index (0…n-1) — for dots / thumbnails. */
  active: number;
  /** Whether the track should animate (false only during the seamless snap). */
  animate: boolean;
  /** Advance by ±1 (or any delta). */
  go: (dir: number) => void;
  /** Jump to a logical item index. */
  goTo: (index: number) => void;
  /** Attach to the track's `onTransitionEnd` to perform the wrap-snap. */
  onTransitionEnd: () => void;
}

/**
 * Seamless, infinite-loop carousel state for a horizontally-sliding track.
 *
 * Renders clones of the last and first items at each end; advancing past an end
 * lands on a clone, then on transition-end it snaps (without animation) back to
 * the matching real slide — so the loop is continuous with no "rewind" jump.
 * Only meaningful for 2+ items; guard the render for 0/1 items (in which case
 * `slides` is just the items and `pos`/`active` stay at the first).
 */
export function useInfiniteCarousel<T>(
  items: T[],
  { auto = false, intervalMs = 3200, paused = false }: { auto?: boolean; intervalMs?: number; paused?: boolean } = {},
): InfiniteCarousel<T> {
  const n = items.length;
  const looping = n > 1;
  // Physical index: 0 and n+1 are the clones; real slides occupy 1…n.
  const [pos, setPos] = useState(looping ? 1 : 0);
  const [animate, setAnimate] = useState(true);

  const slides = looping ? [items[n - 1]!, ...items, items[0]!] : items;
  const active = looping ? (pos - 1 + n) % n : 0;

  const go = useCallback((dir: number) => {
    setAnimate(true);
    setPos((p) => p + dir);
  }, []);

  const goTo = useCallback((index: number) => {
    setAnimate(true);
    setPos(index + 1);
  }, []);

  // Auto-advance forward on an interval.
  useEffect(() => {
    if (!auto || !looping || paused) return;
    const id = window.setInterval(() => {
      setAnimate(true);
      setPos((p) => p + 1);
    }, intervalMs);
    return () => window.clearInterval(id);
  }, [auto, looping, paused, intervalMs]);

  // When a slide lands on a clone, snap (no animation) to its real counterpart.
  const onTransitionEnd = useCallback(() => {
    if (!looping) return;
    if (pos === n + 1) {
      setAnimate(false);
      setPos(1);
    } else if (pos === 0) {
      setAnimate(false);
      setPos(n);
    }
  }, [pos, n, looping]);

  // Re-enable the transition a frame after a snap, so the snap itself is instant.
  useEffect(() => {
    if (animate) return;
    const raf = requestAnimationFrame(() => requestAnimationFrame(() => setAnimate(true)));
    return () => cancelAnimationFrame(raf);
  }, [animate]);

  return { slides, pos, active, animate, go, goTo, onTransitionEnd };
}
